import {
  type NamedHierarchyNode,
  type TeamMemberRecord,
  toTeamNode,
} from "@contract-manager/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { ApiClient } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { describeError, isSignedOutError } from "../auth/authErrors";
import { useCurrentUser } from "../auth/CurrentUser";
import { AdminTeamBadge } from "../components/AdminBadge";
import { Alert } from "../components/Alert";
import { ConfirmDialog } from "../components/Dialog";
import {
  HierarchyAdmin,
  type NodeOperations,
} from "../components/HierarchyAdmin";
import { useI18n } from "../i18n/I18n";

/**
 * チーム管理: the team tree on the left, the selected team's roster on the
 * right.
 *
 * Members are added from the user directory (`/users`), not from here: someone
 * can only join a team if they have signed in at least once, and the directory
 * is what knows that. This screen shows who is on a team and can take them
 * off — removal is safe from either side.
 *
 * Everyone can browse; changing teams and rosters is for administrators.
 */
export function TeamAdminPage({ api }: { api: ApiClient }) {
  const { isAdmin } = useCurrentUser();
  const { t } = useI18n();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Written by `list` but read only while rendering, through `badge`, so it
  // can sit outside the memo below without reloading the tree on every load.
  const adminTeamIds = useRef<ReadonlySet<string>>(new Set());

  const operations = useMemo<NodeOperations>(
    () => ({
      kind: "team",
      list: async () => {
        const { items } = await api.listTeams();
        adminTeamIds.current = new Set(
          items.filter((team) => team.isAdminTeam).map((team) => team.teamId),
        );
        return items.map(toTeamNode);
      },
      badge: (id) => (adminTeamIds.current.has(id) ? <AdminTeamBadge /> : null),
      create: (input) => api.createTeam(input),
      update: (id, changes) => api.updateTeam(id, changes),
      remove: (id) => api.deleteTeam(id),
    }),
    [api],
  );

  return (
    <div className="ds-rows --gap-medium">
      <div className="ds-cols --baseline --gap-small">
        <h1>{t("organisation.teams.title")}</h1>
        <Link to="/" className="ds-button --plain --small">
          <span>{t("common.backToList")}</span>
        </Link>
      </div>

      <HierarchyAdmin
        operations={operations}
        canManage={isAdmin}
        selectedId={selectedId}
        onSelectedChange={setSelectedId}
      >
        {(team) => (
          <TeamMembers
            api={api}
            team={team}
            isAdminTeam={adminTeamIds.current.has(team.id)}
            canManage={isAdmin}
          />
        )}
      </HierarchyAdmin>
    </div>
  );
}

function TeamMembers({
  api,
  team,
  isAdminTeam,
  canManage,
}: {
  api: ApiClient;
  team: NamedHierarchyNode;
  isAdminTeam: boolean;
  canManage: boolean;
}) {
  const auth = useAuth();
  const { t, formatTimestamp } = useI18n();
  const [members, setMembers] = useState<TeamMemberRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setMembers((await api.listTeamMembers(team.id)).items);
      setError(null);
    } catch (caught) {
      if (isSignedOutError(caught)) {
        auth.reportSignedOut();
        return;
      }
      setError(describeError(caught, t));
    } finally {
      setLoading(false);
    }
  }, [api, auth, team.id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      setBusy(true);
      setError(null);
      try {
        await action();
        await load();
        return true;
      } catch (caught) {
        if (isSignedOutError(caught)) {
          auth.reportSignedOut();
          return false;
        }
        setError(describeError(caught, t));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [auth, load, t],
  );

  return (
    <>
      <div className="ds-cols --middle --gap-small">
        <h2 className="ds-text --large">{team.name}</h2>
        {isAdminTeam && <AdminTeamBadge />}
      </div>

      {isAdminTeam && (
        <p className="ds-text --small --muted">
          {t("organisation.teams.adminTeamNote")}
        </p>
      )}

      {canManage && (
        <div className="ds-notice --light">
          <p>
            {t("organisation.teams.addMembersBefore")}
            <Link to="/users">{t("organisation.teams.addMembersLink")}</Link>
            {t("organisation.teams.addMembersAfter")}
          </p>
        </div>
      )}

      {error !== null && <Alert tone="failed">{error}</Alert>}

      {loading && <p className="ds-text --muted">{t("common.loading")}…</p>}

      {!loading && members.length === 0 && (
        <p className="ds-text --muted">{t("organisation.teams.noMembers")}</p>
      )}

      {members.length > 0 && (
        <ul className="app-list">
          {members.map((member) => (
            <li
              key={member.email}
              className="ds-cols --middle --nowrap --gap-small"
            >
              <span className="app-mono app-truncate app-grow">
                {member.email}
              </span>
              <span className="ds-text --small --muted app-mono">
                {formatTimestamp(member.addedAt)}
              </span>
              {canManage && (
                <button
                  type="button"
                  className="ds-button --xsmall --alert"
                  disabled={busy}
                  onClick={() => setLeaving(member.email)}
                >
                  <span>{t("common.remove")}</span>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {leaving !== null && (
        <ConfirmDialog
          title={t("organisation.teams.removeTitle", { email: leaving })}
          description={t("organisation.teams.removeDescription", {
            email: leaving,
            team: team.name,
          })}
          confirmLabel={t("common.remove")}
          onConfirm={() => {
            setLeaving(null);
            void run(() => api.removeTeamMember(team.id, leaving));
          }}
          onCancel={() => setLeaving(null)}
        />
      )}
    </>
  );
}
